import React, { createContext, useCallback, useMemo } from 'react'
import InsightsDashboardPool, {
  AccessKeyDuck,
  DashboardDuck
} from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import usePool from '@duckness/use-pool'
import type { UseDashboardReturnType } from './types'
// import getBlockModelByType from '@aytm/app/insights/dashboard/models/blocks'
import { makeRequestShareDialogDashboardAction } from '@aytm/app/insights/dashboard/duckness/ducks/DashboardDuck/DashboardDuck.actionCreators'
import type { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { defaultMinimumSampleSize, defaultPreset } from '@aytm/app/insights/dashboard/lib/config'

import {
  EDITOR_FILTERS_MASK,
  PANEL_FILTERS_MASK,
  PORTAL_FILTERS_MASK,
  SHARED_FILTERS_MASK,
  VIEWER_FILTERS_MASK
} from '@aytm/app/insights/dashboard/lib/constants'
import { FilterValuesListModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import type { FilterValuesListTypeModel } from '@aytm/app/insights/dashboard/models/types.ClientModel'
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'
import {
  clonePortal,
  dismissShareDialog,
  fetchAccessKey,
  requestShareURL,
  sendInvitationLink,
  sendSchema,
  sendSource,
  updateDashboard,
  updateStore
} from './useDashboard.logic'

export const DashboardContext = createContext<UseDashboardReturnType>(null)

export const DashboardProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dashboardAPI = useDashboardInternal()
  return <DashboardContext.Provider value={dashboardAPI}>{children}</DashboardContext.Provider>
}

export function useDashboardInternal(): UseDashboardReturnType {
  const isSharedView = staticStore.isSharedView

  const accessKey = usePool(InsightsDashboardPool, AccessKeyDuck.select.accessKey)
  const roles = usePool(InsightsDashboardPool, AccessKeyDuck.select.roles)
  const error = usePool(InsightsDashboardPool, DashboardDuck.select.error)
  const invitationError = usePool(InsightsDashboardPool, DashboardDuck.select.invitationError)
  const shareRequested = usePool(InsightsDashboardPool, DashboardDuck.select.shareRequested)
  const shareURLs = usePool(InsightsDashboardPool, DashboardDuck.select.shareURLs)
  const editorMode = useMemo(() => roles && roles.includes('editor'), [roles])
  const blockModel = usePool(InsightsDashboardPool, DashboardDuck.select.currentBlockModel)
  const blockCount = usePool(InsightsDashboardPool, DashboardDuck.select.blockCount)
  const blocks = usePool(InsightsDashboardPool, DashboardDuck.select.blocks)
  const currentSchema = usePool(InsightsDashboardPool, DashboardDuck.select.currentSchema)
  const remoteSchema = usePool(InsightsDashboardPool, DashboardDuck.select.remoteSchema)
  const activeTabID = usePool(InsightsDashboardPool, DashboardDuck.select.activeTabID)
  const userTimeZone = usePool(InsightsDashboardPool, DashboardDuck.select.userTimeZone)

  const showSidebar = usePool(InsightsDashboardPool, DashboardDuck.select.showSidebar)
  const invitationStatus = usePool(InsightsDashboardPool, DashboardDuck.select.invitationStatus)

  const blockUID = useMemo(() => blockModel?.getObject('uid') || null, [blockModel])
  const blockType = useMemo(() => blockModel?.getObject('type') || null, [blockModel])
  const currentPreset = useMemo(() => blockModel?.getObject('currentPreset') || defaultPreset, [blockModel])
  // const BlockModel = useMemo(() => getBlockModelByType(blockType), [blockType])
  const hideBlankColumns = useMemo(() => blockModel?.hideBlankColumns || false, [blockModel])

  const fullFiltersMask = useMemo(
    () => (isSharedView ? SHARED_FILTERS_MASK : editorMode ? EDITOR_FILTERS_MASK : VIEWER_FILTERS_MASK),
    [editorMode, isSharedView]
  )

  const { portalCurrentFilters, panelCurrentFilters, hiddenItems } = usePool(
    InsightsDashboardPool,
    (state: InsightsDashboardPoolState) => {
      const store = state.currentData
      return {
        portalCurrentFilters: store?.portalCurrentFilters || null,
        panelCurrentFilters: store?.panelCurrentFilters || null,
        hiddenItems: store?.hiddenItems || []
      }
    },
    (prev, next) =>
      !prev && !next
        ? false
        : (!prev && Boolean(next)) ||
          (prev && !next) ||
          prev.portalCurrentFilters !== next.portalCurrentFilters ||
          prev.panelCurrentFilters !== next.panelCurrentFilters ||
          prev.hiddenItems !== next.hiddenItems
  )

  const accountType = staticStore?.accountType

  const isTeamAccount = useMemo(
    () => (accountType ? ['singleteam', 'multiteam'].includes(accountType) : false),
    [accountType]
  )

  const minSampleSize = useMemo(() => {
    if (blockModel) {
      const filters = blockModel.getFilters()
      const sample = Number(
        filters?.getIn<FilterValuesListTypeModel>(['sample:low_size_cutoff', 'values'], FilterValuesListModel())?.get(0)
      )
      return isNaN(sample) ? defaultMinimumSampleSize : sample
    }
    return defaultMinimumSampleSize
  }, [blockModel])

  // const { sendSource, sendSchema } = usePortalSurveys()

  const requestShareDialog = useCallback(
    (reportId: string) => {
      poolDispatch(makeRequestShareDialogDashboardAction(shareRequested === reportId ? null : reportId))
      // InsightsDashboardPool.dispatch(
      //   'dashboard',
      //   DASHBOARD_ACTIONS.REQUEST_SHARE_UI,
      //   shareRequested === reportId ? null : reportId
      // )
    },
    [shareRequested]
  )

  const isErrorRequestingShareURL = usePool(InsightsDashboardPool, DashboardDuck.select.shareKeyRequestError)

  // const sendInvitationLink = useRef(
  //   /**
  //    * @type {UseDashboardReturnType['sendInvitationLink']}
  //    */
  //   (sharedKey, email, sendStatusReset) =>
  //     sendStatusReset
  //       ? poolDispatch(makeSendInvitationResetStatusDashboardAction())
  //       : // InsightsDashboardPool.dispatch('dashboard', DASHBOARD_ACTIONS.SEND_INVITATION, {
  //         //     sendStatus: SendStatus.Send
  //         //   })
  //         poolDispatch(makeSendInvitationForEmailDashboardAction(email, sharedKey))
  //   // InsightsDashboardPool.dispatch('dashboard', DASHBOARD_ACTIONS.SEND_INVITATION, { sharedKey, email })
  // )
  /**
   * @type {UseDashboardReturnType}
   */
  return useMemo<UseDashboardReturnType>(
    () =>
      ({
        accessKey,
        accountType,
        blockCount,
        blockModel,
        // BlockModel,
        blocks,
        currentSchema,
        currentPreset,
        remoteSchema,
        blockType,
        blockUID,
        clonePortal,
        activeTabID,
        userTimeZone,
        showSidebar,
        dismissShareDialog,
        editorMode,
        viewerMode: !editorMode,
        hiddenItems,
        hideBlankColumns,
        error,
        invitationError,
        fetchAccessKey,
        fullFiltersMask,
        invitationStatus,
        isTeamAccount,
        minSampleSize,
        panelFiltersMask: PANEL_FILTERS_MASK,
        portalFiltersMask: PORTAL_FILTERS_MASK,
        requestShareDialog,
        requestShareURL,
        isErrorRequestingShareURL,
        roles,
        sendInvitationLink,
        shareRequested,
        shareURLs,
        updateStore,
        sendSource,
        sendSchema,
        canUpdateDashboard: staticStore.canUpdateDashboard,
        isSharedView,
        updateDashboard,
        portalCurrentFilters,
        panelCurrentFilters
      }) as UseDashboardReturnType,
    [
      accessKey,
      accountType,
      activeTabID,
      blockCount,
      blockModel,
      blockType,
      blockUID,
      blocks,
      currentPreset,
      currentSchema,
      editorMode,
      error,
      fullFiltersMask,
      hiddenItems,
      hideBlankColumns,
      invitationError,
      invitationStatus,
      isErrorRequestingShareURL,
      isSharedView,
      isTeamAccount,
      minSampleSize,
      panelCurrentFilters,
      portalCurrentFilters,
      remoteSchema,
      requestShareDialog,
      roles,
      shareRequested,
      shareURLs,
      showSidebar,
      userTimeZone
    ]
  )
}
